/**
 * «Qué se le vendió»: el selector de producto del formulario de Ventas.
 *
 * Es el widget `catalogo` del campo `product` de `sales.subscription`, y es la
 * respuesta al pedido textual del dueño: «en la sección de Ventas, en "Qué se le
 * vendió", cuando carguemos los productos estaría bueno que aparezcan todas las
 * opciones de ventas que hay con los precios que carguemos».
 *
 * Qué arregla
 * -----------
 * Hasta hoy «Qué se le vendió» era un input de TEXTO LIBRE y el importe se
 * tipeaba de memoria. Eso tenía tres costos, en orden de gravedad:
 *
 *  1. **«Llamar el» quedaba vacío.** Sin producto vinculado no hay de dónde
 *     sacar `Product.expiry_from()`, y esa es la columna que el dueño mira todos
 *     los días.
 *  2. El precio salía de la memoria de quien vendía, no de la lista de precios
 *     del cliente.
 *  3. El mismo trámite escrito de cinco formas hacía imposible contar o totalizar
 *     por producto.
 *
 * Un componente, dos puertas
 * --------------------------
 * La lista de productos que dibuja acá es **la misma** que la del diálogo de
 * Venta rápida (`components/venta/catalogo`), alimentada por **la misma** acción
 * de servidor. No hay dos selectores: hay uno con dos puertas. Dos habrían sido
 * dos precios distintos el día que uno se quedara viejo.
 *
 * Por qué no el many2one genérico
 * -------------------------------
 * `RelationPicker` no acepta dominio, así que ofrecería productos archivados, no
 * vendibles y de otras empresas, y sobre todo mostraría **sólo el nombre**. El
 * dueño pidió el catálogo *con los precios*. La acción del catálogo ya filtra por
 * `active`, `can_be_sold` y empresa, y ya trae el precio resuelto para ESTE
 * cliente.
 *
 * A qué fecha cotiza
 * ------------------
 * A la de «Vendido el», nunca a la de hoy. El precio sale de la lista que regía
 * ese día y «Llamar el» se cuenta desde ese día, así que el catálogo se le pide
 * al servidor **con esa fecha**. Y si la fecha se corrige después de haber
 * elegido el producto —que es lo normal: el selector está arriba y la fecha
 * abajo— los dos se vuelven a calcular. Sin eso, cargar la cartera existente con
 * su fecha real dejaba «Llamar el» mal en todas: cinco meses de vencimiento para
 * un trámite trimestral, en silencio.
 *
 * La regla que gobierna todo lo que escribe
 * -----------------------------------------
 * Al elegir un producto se completan el detalle, el precio unitario, el importe,
 * el vencimiento, la frecuencia y la intención de renovar — **pero sólo los
 * campos que están vacíos o que escribió este mismo widget**. Nunca pisa un
 * número que ya estaba. Es lo que hace que enganchar una venta vieja a su
 * producto no le cambie el importe que el cliente ya tiene pactado (y puede que
 * ya facturado), y a la vez que cambiar de producto A a producto B en un alta
 * nueva sí recotice, porque esos valores los había escrito el widget.
 *
 * Todos los campos que toca siguen siendo campos normales del formulario:
 * quedan editables, se pueden pisar, y el precio de lista tachado al lado dice
 * de qué se está apartando quien los pisa.
 */

import { useEffect, useRef, useState } from 'react';

import { controlClass } from '@/components/fields/controls';
import { ReadonlyOutput } from '@/components/fields/FieldDisplay';
import { isMany2One } from '@/components/fields/format';
import type { FieldInputProps } from '@/components/fields/types';
import {
  AvisoDeCatalogoTruncado,
  FichaDelProducto,
  fechaDeVenta,
  filtrarCatalogo,
  importeDe,
  useCatalogoDeVenta,
} from '@/components/venta/catalogo';
import type { ProductoOfrecido } from '@/components/venta/catalogo';
import { IconAlert, IconSearch } from '@/components/shell/icons';
import { EmptyState, LoadingState } from '@/components/shell/primitives';
import { ApiError } from '@/lib/api';
import type { FieldValue, Many2OneValue } from '@/lib/types';

/** Los campos hermanos que este widget completa. Ninguno más. */
const CAMPOS_QUE_COMPLETA = [
  'name',
  'unit_price',
  'amount',
  // «Vendido el» se completa por lo mismo que se completa «Llamar el», y le
  // faltaba: el catálogo COTIZA A UNA FECHA —el precio sale de la lista que
  // regía ese día y el vencimiento se cuenta desde ahí— y cuando el campo está
  // vacío esa fecha es hoy. Escribirla es dejar anotado contra qué se cotizó.
  //
  // Sin esto la pantalla se contradecía sola: elegías el trámite, «Llamar el»
  // aparecía calculado (13/08/2027), «Vendido el» quedaba en blanco, y el
  // primer «Crear» rebotaba con «Falta completar Vendido el» sobre un
  // formulario que ya se veía completo. Es lo primero que hace cualquiera que
  // abre el sistema, y el primer cartel que veía era un error suyo que no era
  // suyo.
  'start_date',
  'end_date',
  'renewal_period',
  'auto_renew',
] as const;

type CampoCompletado = (typeof CAMPOS_QUE_COMPLETA)[number];

/**
 * Los dos que siguen al producto SIEMPRE, aunque tengan algo escrito.
 *
 * No son datos: son **consecuencias del producto elegido**. «Frecuencia» nace en
 * "mensual" por el default del modelo y «Se renueva» en sí, así que la regla de
 * "no pisar lo que ya está" no los dejaría corregirse nunca y un trámite
 * trimestral quedaría renovándose todos los meses. Se escriben delante del
 * usuario, en campos que quedan editables y a la vista en la misma pantalla.
 *
 * La plata (`unit_price`, `amount`), la fecha (`end_date`) y el texto (`name`)
 * NO están acá, y esa es toda la diferencia: eso sí se conserva.
 */
const SIGUEN_AL_PRODUCTO = new Set<CampoCompletado>(['renewal_period', 'auto_renew']);

/** Lo último que ESTE widget escribió, para saber qué puede volver a pisar. */
type Escrito = Partial<Record<CampoCompletado, FieldValue>>;

/**
 * Hoy como `AAAA-MM-DD`, en la zona del navegador.
 *
 * Armada a mano y no con `toISOString()`, que convierte a UTC: en Argentina
 * (UTC−3) cualquier venta cargada antes de las 21 h se guardaría con la fecha de
 * mañana, y el vencimiento saldría corrido un día. Es el mismo criterio que usa
 * la agenda del inicio.
 */
function hoyIso(): string {
  const hoy = new Date();
  return [
    hoy.getFullYear(),
    String(hoy.getMonth() + 1).padStart(2, '0'),
    String(hoy.getDate()).padStart(2, '0'),
  ].join('-');
}

/** `true` si el campo está vacío a los efectos de «no pisar lo que ya está». */
function estaVacio(valor: FieldValue): boolean {
  if (valor === null || valor === undefined) return true;
  if (typeof valor === 'string') return valor.trim() === '' || Number(valor) === 0;
  if (typeof valor === 'number') return valor === 0;
  if (typeof valor === 'boolean') return valor === false;
  return false;
}

/** Texto de la cantidad que hay en el formulario. Sin nada cargado, «1». */
function cantidadDe(valores: Record<string, FieldValue>): string {
  const bruto = valores.quantity;
  if (bruto === null || bruto === undefined) return '1';
  const texto = String(bruto).trim();
  return texto === '' ? '1' : texto;
}

export function ProductoVendidoField({
  id,
  meta,
  value,
  onChange,
  readonly,
  invalid,
  describedBy,
  siblings,
}: FieldInputProps) {
  const [busqueda, setBusqueda] = useState('');
  const [eligiendo, setEligiendo] = useState(false);
  /** El usuario eligió a propósito la salida «no está en la lista». */
  const [fueraDelCatalogo, setFueraDelCatalogo] = useState(false);
  /** Lo que este widget escribió en los campos hermanos. Ver el docstring. */
  const escrito = useRef<Escrito>({});

  const elegido: Many2OneValue | null = isMany2One(value) ? value : null;
  const valores = siblings?.values ?? {};
  const cliente: Many2OneValue | null = isMany2One(valores.partner) ? valores.partner : null;
  const partnerId = cliente?.id ?? null;

  /**
   * La fecha de la venta, que es la fecha a la que se cotiza TODO lo de abajo.
   *
   * No es un detalle: el vencimiento se cuenta desde el día en que se vendió y
   * el precio sale de la lista que regía ese día. Pidiendo el catálogo sin
   * fecha el servidor contesta con la de hoy, y entonces una venta cargada con
   * su fecha real —la de ayer, la de la semana pasada, la de la cartera que se
   * está pasando al sistema— quedaba con «Llamar el» mal y sin aviso: un trámite
   * trimestral vendido el 01/06 salía con llamado el 07/11 en vez del 01/09.
   */
  const fechaDeLaVenta = fechaDeVenta(valores.start_date);

  const catalogo = useCatalogoDeVenta(readonly ? null : partnerId, fechaDeLaVenta);
  const productos = catalogo.data?.products ?? [];
  const visibles = filtrarCatalogo(productos, busqueda);
  /** El producto elegido, tal como lo cotiza el catálogo A LA FECHA DE LA VENTA. */
  const cotizado =
    elegido === null ? undefined : productos.find((p) => p.product_id === elegido.id);

  /**
   * ¿Se puede escribir este campo hermano?
   *
   * Sí si está vacío, o si el valor que tiene es exactamente el que escribió
   * este widget la vez anterior. Es toda la protección de «no re-cotizar»: un
   * importe que puso una persona, una importación o una renovación no coincide
   * con lo que el widget anotó, así que no se toca.
   */
  const sePuedeEscribir = (campo: CampoCompletado): boolean => {
    if (SIGUEN_AL_PRODUCTO.has(campo)) return true;
    const actual = valores[campo] ?? null;
    if (estaVacio(actual)) return true;
    const mio = escrito.current[campo];
    return mio !== undefined && String(mio) === String(actual);
  };

  const escribirHermanos = (parcial: Escrito): void => {
    if (siblings === undefined) return;
    const aEscribir: Record<string, FieldValue> = {};
    for (const [campo, valor] of Object.entries(parcial)) {
      if (!sePuedeEscribir(campo as CampoCompletado)) continue;
      aEscribir[campo] = valor as FieldValue;
      escrito.current[campo as CampoCompletado] = valor as FieldValue;
    }
    if (Object.keys(aEscribir).length > 0) siblings.setValues(aEscribir);
  };

  const elegirProducto = (producto: ProductoOfrecido): void => {
    setFueraDelCatalogo(false);
    setEligiendo(false);
    onChange({ id: producto.product_id, display_name: producto.name });

    const cantidad = cantidadDe(valores);
    const importe = importeDe(producto.price, cantidad);
    escribirHermanos({
      name: producto.name,
      unit_price: producto.price,
      ...(importe === null ? {} : { amount: importe }),
      // La fecha CONTRA LA QUE SE COTIZÓ. Va por `escribirHermanos`, así que la
      // regla de «no pisar lo que ya está» la protege igual que a la plata: si
      // el usuario ya puso una fecha —la venta de la semana pasada que está
      // cargando— no se toca, y de hecho es esa la que el catálogo usó para
      // cotizar. Sólo se escribe cuando está vacía, y entonces lo que se
      // escribe es hoy, que es exactamente lo que el servidor supuso.
      start_date: fechaDeLaVenta ?? hoyIso(),
      // Sin recurrencia el vencimiento queda VACÍO y no se inventa una fecha: un
      // trámite de una sola vez no se renueva, y meterle vencimiento lo haría
      // aparecer en «a quién llamar», que es la pantalla que el dueño mira todos
      // los días.
      end_date: producto.expiry_date,
      renewal_period: producto.renewal_period,
      auto_renew: producto.expiry_date !== null,
    });
  };

  const salirDelCatalogo = (): void => {
    setFueraDelCatalogo(true);
    setEligiendo(false);
    onChange(null);
  };

  /**
   * Si se corrige «Vendido el», el vencimiento y el precio se vuelven a cotizar.
   *
   * El orden en el que se cargan los campos no puede cambiar el resultado. En el
   * formulario el producto está arriba y la fecha abajo, así que lo normal es
   * elegir el trámite primero y corregir la fecha después; sin esto, el
   * vencimiento se quedaba con el que se calculó a la fecha vieja y nada avisaba.
   *
   * `escribirHermanos` sigue mandando: sólo se reescribe lo que está vacío o lo
   * que escribió este mismo widget, así que un importe pactado a mano o un
   * vencimiento corregido a mano no se tocan.
   *
   * La primera vuelta **no escribe nada**: es el estado con el que se abrió la
   * pantalla —una venta ya guardada, con su fecha y su vencimiento— y ahí no hay
   * ninguna corrección que seguir.
   */
  const cotizacion = useRef<string | null>(null);
  useEffect(() => {
    if (siblings === undefined) return;
    if (cotizado === undefined) return;
    const firma = `${fechaDeLaVenta ?? ''}|${cotizado.product_id}`;
    const previa = cotizacion.current;
    cotizacion.current = firma;
    if (previa === null || previa === firma) return;

    const importe = importeDe(cotizado.price, cantidadDe(valores));
    escribirHermanos({
      unit_price: cotizado.price,
      ...(importe === null ? {} : { amount: importe }),
      end_date: cotizado.expiry_date,
    });
    // `valores` cambia de identidad en cada render; lo que mueve la cotización
    // es la fecha de la venta y el producto elegido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaDeLaVenta, cotizado]);

  /**
   * Si cambia la cantidad, el importe la sigue — **mientras el importe siga
   * siendo el que escribió el widget**. Apenas alguien lo pisa a mano, deja de
   * moverse: la cuenta la hace el sistema hasta que una persona decide otra cosa.
   */
  const cantidad = cantidadDe(valores);
  const precioUnitario = valores.unit_price;
  useEffect(() => {
    if (siblings === undefined) return;
    if (escrito.current.amount === undefined) return;
    if (precioUnitario === null || precioUnitario === undefined) return;
    const importe = importeDe(String(precioUnitario), cantidad);
    if (importe === null) return;
    const actual = valores.amount ?? null;
    if (actual !== null && String(actual) === importe) return;
    if (!sePuedeEscribir('amount')) return;
    escrito.current.amount = importe;
    siblings.setValues({ amount: importe });
    // `valores` cambia en cada render por identidad; las dependencias reales son
    // la cantidad y el precio, que son los que mueven la cuenta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cantidad, precioUnitario]);

  if (readonly) {
    return (
      <ReadonlyOutput
        id={id}
        meta={meta}
        value={value}
        {...(describedBy === undefined ? {} : { describedBy })}
      />
    );
  }

  // ---------------------------------------------------------------------
  // Ya hay un producto elegido: se muestra y se puede cambiar
  // ---------------------------------------------------------------------
  if (elegido !== null && !eligiendo) {
    return (
      <div className="space-y-1">
        <div className="flex items-stretch gap-1.5">
          <button
            id={id}
            type="button"
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            onClick={() => {
              setEligiendo(true);
            }}
            className="touch-target flex min-w-0 flex-1 items-center rounded-md border border-line bg-surface px-3 py-2 text-left text-sm transition-colors hover:border-line-strong sm:h-9 sm:min-h-0 sm:py-1.5"
          >
            <span className="truncate">{elegido.display_name}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setEligiendo(true);
            }}
            className="shrink-0 rounded-md border border-line px-3 text-sm text-fg-muted transition-colors hover:border-line-strong hover:text-fg"
          >
            Cambiar
          </button>
        </div>
        <p className="text-xs text-fg-faint">
          El importe y «Llamar el» salieron de este producto, de la lista de precios del cliente y
          de la fecha de «Vendido el». Se pueden corregir abajo; lo que se factura es el importe.
        </p>
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // Sin cliente elegido no hay precios que mostrar, y no se inventa ninguno
  // ---------------------------------------------------------------------
  if (partnerId === null) {
    return (
      <p
        id={id}
        aria-describedby={describedBy}
        className="rounded-md border border-dashed border-line px-3 py-2 text-sm text-fg-muted"
      >
        Elegí primero el cliente: el precio depende de su lista de precios, y mostrar el de
        catálogo sería prometer un número que la factura no va a repetir.
      </p>
    );
  }

  // ---------------------------------------------------------------------
  // La salida elegida a propósito: fuera del catálogo
  // ---------------------------------------------------------------------
  if (fueraDelCatalogo) {
    return (
      <div className="space-y-2">
        <div
          id={id}
          aria-describedby={describedBy}
          className="rounded-md border border-dashed border-warning/60 bg-warning-soft/20 px-3 py-2 text-sm"
        >
          <p className="font-medium">Fuera del catálogo</p>
          <p className="mt-1 text-xs text-fg-muted">
            Escribí abajo, en «Detalle», qué se le vendió. Sin producto del catálogo el precio y el
            vencimiento se cargan a mano: el sistema no tiene de dónde sacarlos, así que esta venta
            no va a avisar cuándo hay que llamar. Si es un trámite que se repite, cargalo en Ventas
            ▸ Planes y servicios y la próxima sale solo.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setFueraDelCatalogo(false);
          }}
          className="text-xs font-medium text-accent underline-offset-2 hover:underline"
        >
          Volver al catálogo
        </button>
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // El selector
  // ---------------------------------------------------------------------
  return (
    <div
      id={id}
      role="group"
      aria-labelledby={`${id}-label`}
      aria-describedby={describedBy}
      className="rounded-md border border-line bg-surface"
    >
      <div className="border-b border-line p-2">
        <div className="relative">
          <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-faint" />
          <input
            type="search"
            value={busqueda}
            onChange={(evento) => {
              setBusqueda(evento.target.value);
            }}
            placeholder="Buscar por nombre, código o rubro…"
            aria-label="Buscar en el catálogo"
            className={controlClass(false, 'pl-9')}
          />
        </div>
        {catalogo.data?.pricelist_name !== undefined && catalogo.data.pricelist_name !== '' && (
          <p className="mt-1.5 px-1 text-xs text-fg-faint">
            Precios de la lista «{catalogo.data.pricelist_name}» de {catalogo.data.partner_name}.
          </p>
        )}
      </div>

      <div className="max-h-72 overflow-y-auto">
        {catalogo.isPending ? (
          <LoadingState label="Buscando los precios de este cliente…" />
        ) : catalogo.isError ? (
          <div className="flex items-start gap-3 p-4">
            <IconAlert className="h-5 w-5 shrink-0 text-danger" />
            <div>
              <p className="text-sm font-semibold">No se pudo abrir el catálogo</p>
              <p className="mt-1 text-xs text-fg-muted">
                {catalogo.error instanceof ApiError
                  ? catalogo.error.message
                  : 'Reintentá en un momento.'}{' '}
                Mientras tanto se puede cargar a mano con la salida de abajo.
              </p>
            </div>
          </div>
        ) : visibles.length === 0 ? (
          <EmptyState
            title={
              productos.length === 0 ? 'No hay productos para vender' : 'Ningún producto coincide'
            }
            description={
              productos.length === 0
                ? 'Cargá los trámites que vendés en Ventas ▸ Planes y servicios, con su precio y cada cuánto se renuevan, y van a aparecer acá.'
                : 'Probá con otra parte del nombre, el código o el rubro.'
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {visibles.map((producto) => (
              <li key={producto.product_id}>
                <button
                  type="button"
                  onClick={() => {
                    elegirProducto(producto);
                  }}
                  className="block w-full px-4 py-2.5 text-left transition-colors hover:bg-app-subtle"
                >
                  <FichaDelProducto producto={producto} />
                </button>
              </li>
            ))}
          </ul>
        )}
        {catalogo.data?.truncated === true && <AvisoDeCatalogoTruncado />}
      </div>

      {/* LA SALIDA, AL FINAL Y A PROPÓSITO.
          No es un campo de texto esperando al lado del selector —eso sería la
          invitación a seguir escribiendo a mano—: hay que bajar hasta acá y
          elegirla. Se puede cargar lo que no está en la lista, pero se nota que
          es la excepción. */}
      <div className="border-t border-line p-2">
        <button
          type="button"
          onClick={salirDelCatalogo}
          className="w-full rounded-md px-3 py-2 text-left text-sm text-fg-muted transition-colors hover:bg-app-subtle hover:text-fg"
        >
          No está en la lista — cargarlo igual como texto
        </button>
      </div>
    </div>
  );
}
